import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Loader2, FileText, Search, Filter, Download, Eye, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { SessionActivitySheet } from './activity/SessionActivitySheet';
import { UserActivityTab } from './activity/UserActivityTab';
import { RejectedRequestsTab } from './activity/RejectedRequestsTab';

const AuditLogs = () => {
  const { token, backendUrl } = useAuth();
  const [activeTab, setActiveTab] = useState('access');
  const [accessLogs, setAccessLogs] = useState([]);
  const [transactionsLogs, setTransactionsLogs] = useState([]);
  const [apiIngestLogs, setApiIngestLogs] = useState([]);
  const [aiAgentLogs, setAiAgentLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [openSessionId, setOpenSessionId] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  const API = `${backendUrl}/api`;

  // Export access logs to Excel
  const exportAccessLogsToExcel = async () => {
    if (!token) return;
    
    setExporting(true);
    try {
      // Fetch all logs for export (up to 1000 - API limit)
      const params = new URLSearchParams({
        page: 1,
        per_page: 1000,
      });
      if (searchTerm) params.append('search', searchTerm);
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);

      const response = await axios.get(`${API}/audit/access-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const logs = response.data.logs || [];
      
      if (logs.length === 0) {
        alert('No logs to export');
        setExporting(false);
        return;
      }

      // Local date formatter for export
      const formatExportDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      };

      // Create CSV content
      const headers = ['Timestamp', 'User', 'Email', 'IP Address', 'Status', 'Password (failed)', 'Menus', 'Actions', 'Changes', 'User Agent'];
      const csvRows = [headers.join(',')];
      
      logs.forEach(log => {
        const row = [
          formatExportDate(log.timestamp),
          `"${(log.user_name || '').replace(/"/g, '""')}"`,
          log.email || '',
          log.ip_address || '',
          log.status || '',
          `"${(log.password_attempt || '').replace(/"/g, '""')}"`,
          log.page_count ?? 0,
          log.activity_count ?? 0,
          log.change_count ?? 0,
          `"${(log.user_agent || '').replace(/"/g, '""')}"`
        ];
        csvRows.push(row.join(','));
      });

      // Create and download file
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `access_logs_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error exporting access logs:', err);
      alert('Failed to export logs. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const tabs = [
    { id: 'access', label: 'Access Logs', icon: '🔐' },
    { id: 'user-activity', label: 'User Activity', icon: '🧭' },
    { id: 'rejected', label: 'Rejected Requests', icon: '🛡️' },
    { id: 'transactions', label: 'Transactions Log', icon: '📋' },
    { id: 'api-ingest', label: 'API Ingest Logs', icon: '📥' },
    { id: 'ai-agent', label: 'AI Agent Logs', icon: '🤖' }
  ];

  useEffect(() => {
    if (activeTab === 'access') {
      fetchAccessLogs();
    } else if (activeTab === 'transactions') {
      fetchTransactionsLogs();
    } else if (activeTab === 'api-ingest') {
      fetchApiIngestLogs();
    } else if (activeTab === 'ai-agent') {
      fetchAiAgentLogs();
    }
  }, [activeTab, currentPage, perPage, filterStatus]);

  // Debounced search
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (currentPage === 1) {
        if (activeTab === 'access') {
          fetchAccessLogs();
        } else if (activeTab === 'transactions') {
          fetchTransactionsLogs();
        } else if (activeTab === 'api-ingest') {
          fetchApiIngestLogs();
        } else if (activeTab === 'ai-agent') {
          fetchAiAgentLogs();
        }
      } else {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  const fetchAccessLogs = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        per_page: perPage,
      });

      if (searchTerm) params.append('search', searchTerm);
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);

      const response = await axios.get(`${API}/audit/access-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAccessLogs(response.data.logs || []);
      setTotalCount(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching access logs:', err);
      setAccessLogs([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactionsLogs = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        per_page: perPage,
      });

      if (searchTerm) params.append('search', searchTerm);

      const response = await axios.get(`${API}/audit/transactions-log?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTransactionsLogs(response.data.logs || []);
      setTotalCount(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching transactions logs:', err);
      setTransactionsLogs([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchApiIngestLogs = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        per_page: perPage,
      });

      if (searchTerm) params.append('search', searchTerm);

      const response = await axios.get(`${API}/audit/api-ingest-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setApiIngestLogs(response.data.logs || []);
      setTotalCount(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching API ingest logs:', err);
      setApiIngestLogs([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchAiAgentLogs = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: currentPage, per_page: perPage });
      if (searchTerm) params.append('search', searchTerm);
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);
      const response = await axios.get(`${API}/audit/ai-agent-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiAgentLogs(response.data.logs || []);
      setTotalCount(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching AI agent logs:', err);
      setAiAgentLogs([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalCount / perPage);
  
  const formatNumber = (number) => {
    return new Intl.NumberFormat('en-US').format(number);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      success: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      blocked: 'bg-yellow-100 text-yellow-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status?.toUpperCase() || 'UNKNOWN'}
      </span>
    );
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2" style={{ color: 'var(--input-text-color)' }}>
          <FileText className="w-8 h-8 text-orange-500" />
          Audit Logs
        </h1>
        <p className="mt-1" style={{ color: 'var(--text-muted-color)' }}>Monitor system access and activity</p>
      </div>

      {/* Tabs */}
      <div className="border-b" style={{ borderColor: 'var(--border-color)' }}>
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              data-testid={`audit-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm"
              style={{
                borderColor: activeTab === tab.id ? 'var(--button-color)' : 'transparent',
                color: activeTab === tab.id ? 'var(--button-color)' : 'var(--text-muted-color)'
              }}
            >
              <span className="text-xl">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Filters for Access Logs Tab */}
      {activeTab === 'access' && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by email, IP, or user agent..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="md:w-48">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="success">Success</option>
                  <option value="failed">Failed</option>
                  <option value="blocked">Blocked</option>
                </select>
              </div>
              <Button
                data-testid="access-logs-refresh"
                onClick={fetchAccessLogs}
                disabled={loading}
                variant="outline"
                className="flex items-center gap-2"
                title="Refresh"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                onClick={exportAccessLogsToExcel}
                disabled={exporting || loading}
                variant="outline"
                className="flex items-center gap-2"
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Export to Excel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search for Transactions Log Tab */}
      {activeTab === 'transactions' && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by transaction ID, customer name, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* User Activity Tab (own filters + table) */}
      {activeTab === 'user-activity' && (
        <UserActivityTab onOpenSession={(sid) => setOpenSessionId(sid)} />
      )}
      {activeTab === 'rejected' && <RejectedRequestsTab />}

      <SessionActivitySheet sessionId={openSessionId} open={!!openSessionId} onOpenChange={(o) => { if (!o) setOpenSessionId(null); }} />

      {/* Logs Table - Renders for all tabs */}
      {activeTab !== 'user-activity' && activeTab !== 'rejected' && <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span style={{ color: 'var(--text-color)' }}>
                  {activeTab === 'access' ? 'Login Access Logs' : activeTab === 'transactions' ? 'Transactions Log' : activeTab === 'api-ingest' ? 'API Ingest Logs' : 'AI Agent Logs'}
                </span>
                <span className="text-sm font-normal" style={{ color: 'var(--text-muted-color)' }}>
                  {formatNumber(totalCount)} {totalCount === 1 ? 'entry' : 'entries'}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--button-color)' }} />
                  <span className="ml-2" style={{ color: 'var(--text-muted-color)' }}>
                    Loading {activeTab === 'access' ? 'access' : activeTab === 'transactions' ? 'transactions' : 'API ingest'} logs...
                  </span>
                </div>
              ) : activeTab === 'access' && accessLogs.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="mx-auto h-12 w-12" style={{ color: 'var(--text-muted-color)' }} />
                  <h3 className="mt-2 text-sm font-medium" style={{ color: 'var(--text-color)' }}>No access logs found</h3>
                  <p className="mt-1 text-sm" style={{ color: 'var(--text-muted-color)' }}>
                    {searchTerm || filterStatus !== 'all' 
                      ? 'Try adjusting your filters' 
                      : 'Access logs will appear here when users login'}
                  </p>
                </div>
              ) : activeTab === 'transactions' && transactionsLogs.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="mx-auto h-12 w-12" style={{ color: 'var(--text-muted-color)' }} />
                  <h3 className="mt-2 text-sm font-medium" style={{ color: 'var(--text-color)' }}>No transactions logs found</h3>
                  <p className="mt-1 text-sm" style={{ color: 'var(--text-muted-color)' }}>
                    {searchTerm ? 'Try adjusting your search' : 'Deleted transactions will appear here'}
                  </p>
                </div>
              ) : activeTab === 'api-ingest' && apiIngestLogs.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="mx-auto h-12 w-12" style={{ color: 'var(--text-muted-color)' }} />
                  <h3 className="mt-2 text-sm font-medium" style={{ color: 'var(--text-color)' }}>No API ingest logs found</h3>
                  <p className="mt-1 text-sm" style={{ color: 'var(--text-muted-color)' }}>
                    {searchTerm ? 'Try adjusting your search' : 'API bulk transaction imports will appear here'}
                  </p>
                </div>
              ) : activeTab === 'ai-agent' && aiAgentLogs.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="mx-auto h-12 w-12" style={{ color: 'var(--text-muted-color)' }} />
                  <h3 className="mt-2 text-sm font-medium" style={{ color: 'var(--text-color)' }}>No AI Agent logs yet</h3>
                  <p className="mt-1 text-sm" style={{ color: 'var(--text-muted-color)' }}>
                    {searchTerm ? 'Try adjusting your search' : 'AI Agent run progress will appear here when the scheduler or "Run AI Agent Now" fires'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    {activeTab === 'access' ? (
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--filter-label-color)' }}>
                              Detail
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--filter-label-color)' }}>
                              Timestamp
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--filter-label-color)' }}>
                              User
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--filter-label-color)' }}>
                              Email
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--filter-label-color)' }}>
                              IP Address
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--filter-label-color)' }}>
                              Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--filter-label-color)' }}>
                              Password (failed)
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--filter-label-color)' }}>
                              Activity
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--filter-label-color)' }}>
                              User Agent
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {accessLogs.map((log, index) => (
                            <tr key={index} className="hover:bg-gray-50" data-testid="access-log-row">
                              <td className="px-6 py-4 whitespace-nowrap">
                                {log.session_id ? (
                                  <button
                                    data-testid="access-log-details-button"
                                    onClick={() => setOpenSessionId(log.session_id)}
                                    className="inline-flex items-center gap-1 text-xs font-medium hover:underline"
                                    style={{ color: 'var(--button-color)' }}
                                  >
                                    <Eye className="h-3.5 w-3.5" /> Details
                                  </button>
                                ) : (
                                  <span className="text-xs" style={{ color: 'var(--text-muted-color)' }}>—</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-color)' }}>
                                {formatDate(log.timestamp)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-color)' }}>
                                {log.user_name || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-color)' }}>
                                {log.email || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-muted-color)' }}>
                                {log.ip_address || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {getStatusBadge(log.status)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-mono" data-testid="access-log-password-attempt" style={{ color: log.password_attempt ? '#b91c1c' : 'var(--text-muted-color)' }}>
                                {log.status !== 'success' && log.password_attempt ? log.password_attempt : '—'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-xs" data-testid="access-log-activity-summary" style={{ color: 'var(--text-muted-color)' }}>
                                {log.session_id ? (
                                  <span>
                                    <span className="font-semibold" style={{ color: 'var(--text-color)' }}>{log.page_count ?? 0}</span> menus ·{' '}
                                    <span className="font-semibold" style={{ color: 'var(--text-color)' }}>{log.activity_count ?? 0}</span> actions
                                    {log.change_count > 0 && <> · <span className="font-semibold text-amber-700">{log.change_count}</span> changes</>}
                                  </span>
                                ) : '—'}
                              </td>
                              <td className="px-6 py-4 text-sm max-w-xs truncate" style={{ color: 'var(--text-muted-color)' }}>
                                {log.user_agent || 'N/A'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : activeTab === 'transactions' ? (
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap" style={{ color: 'var(--filter-label-color)' }}>
                              Deleted At
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap" style={{ color: 'var(--filter-label-color)' }}>
                              Transaction ID
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap" style={{ color: 'var(--filter-label-color)' }}>
                              Platform
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap" style={{ color: 'var(--filter-label-color)' }}>
                              Customer
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap" style={{ color: 'var(--filter-label-color)' }}>
                              Amount
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap" style={{ color: 'var(--filter-label-color)' }}>
                              Deleted By
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap" style={{ color: 'var(--filter-label-color)' }}>
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {transactionsLogs.map((log, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-color)' }}>
                                {formatDate(log.deleted_at)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-mono" style={{ color: 'var(--text-color)' }}>
                                {log.transaction_data?.transaction_id || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-color)' }}>
                                {log.transaction_data?.platform_name || log.transaction_data?.platform_id || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-color)' }}>
                                {log.transaction_data?.customer_name || log.transaction_data?.customer_email || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-color)' }}>
                                {log.transaction_data?.amount && log.transaction_data?.currency 
                                  ? `${log.transaction_data.amount} ${log.transaction_data.currency}`
                                  : 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-muted-color)' }}>
                                {log.deleted_by || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                  {log.action || 'DELETE'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : activeTab === 'api-ingest' ? (
                      /* API Ingest Logs Table */
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--filter-label-color)' }}>
                              Timestamp
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--filter-label-color)' }}>
                              Organization
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--filter-label-color)' }}>
                              API Key ID
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--filter-label-color)' }}>
                              Transaction IDs
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--filter-label-color)' }}>
                              Total
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--filter-label-color)' }}>
                              Created
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--filter-label-color)' }}>
                              Failed
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--filter-label-color)' }}>
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {apiIngestLogs.map((log, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-color)' }}>
                                {formatDate(log.created_at)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-color)' }}>
                                {log.metadata?.organization || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-mono" style={{ color: 'var(--text-muted-color)' }}>
                                {log.metadata?.api_key_id || 'N/A'}
                              </td>
                              <td className="px-6 py-4 text-sm font-mono" style={{ color: 'var(--text-color)', maxWidth: '200px' }}>
                                {log.metadata?.transaction_ids?.length > 0 ? (
                                  <div className="max-h-20 overflow-y-auto">
                                    {log.metadata.transaction_ids.slice(0, 5).map((id, idx) => (
                                      <div key={idx} className="truncate text-xs">{id}</div>
                                    ))}
                                    {log.metadata.transaction_ids.length > 5 && (
                                      <div className="text-xs text-gray-500">+{log.metadata.transaction_ids.length - 5} more</div>
                                    )}
                                  </div>
                                ) : 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-color)' }}>
                                {log.metadata?.total_count || 0}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <span className="text-green-600 font-medium">
                                  {log.metadata?.created_count || 0}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <span className={log.metadata?.failed_count > 0 ? 'text-red-600 font-medium' : 'text-gray-500'}>
                                  {log.metadata?.failed_count || 0}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {log.metadata?.failed_count === 0 ? (
                                  <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                    Success
                                  </span>
                                ) : log.metadata?.created_count > 0 ? (
                                  <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                    Partial
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                    Failed
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      /* AI Agent Logs Table */
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--filter-label-color)' }}>
                              Timestamp
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--filter-label-color)' }}>
                              Run ID
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--filter-label-color)' }}>
                              Step
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--filter-label-color)' }}>
                              Platform
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--filter-label-color)' }}>
                              Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--filter-label-color)' }}>
                              Message
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {aiAgentLogs.map((log, index) => {
                            const statusColor = log.status === 'error'
                              ? 'bg-red-100 text-red-800'
                              : log.status === 'skipped'
                                ? 'bg-yellow-100 text-yellow-800'
                                : log.status === 'success'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-blue-100 text-blue-800';
                            return (
                              <tr key={log.id || index} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-color)' }}>
                                  {formatDate(log.timestamp)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-xs font-mono" style={{ color: 'var(--text-muted-color)' }}>
                                  {log.run_id || '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: 'var(--text-color)' }}>
                                  {log.step}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-color)' }}>
                                  {log.platform_name || log.platform_id || <span className="text-gray-400">All</span>}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColor}`}>
                                    {log.status || 'info'}
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-sm" style={{ color: 'var(--text-color)', maxWidth: '500px' }}>
                                  <div className="break-words">{log.message || '-'}</div>
                                  {log.details && Object.keys(log.details).length > 0 && (
                                    <div className="mt-1 text-xs" style={{ color: 'var(--text-muted-color)' }}>
                                      {Object.entries(log.details).map(([k, v]) => (
                                        <span key={k} className="mr-2">
                                          {k}: <span className="font-mono">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="mt-4 flex items-center justify-between border-t pt-4" style={{ borderColor: 'var(--border-color)' }}>
                      <div className="text-sm" style={{ color: 'var(--text-muted-color)' }}>
                        Page {currentPage} of {totalPages}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{
                            borderColor: 'var(--border-color)',
                            color: currentPage === 1 ? 'var(--text-muted-color)' : 'var(--button-color)',
                            backgroundColor: currentPage === 1 ? 'transparent' : 'var(--card-bg)'
                          }}
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                          className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{
                            borderColor: 'var(--border-color)',
                            color: currentPage === totalPages ? 'var(--text-muted-color)' : 'var(--button-color)',
                            backgroundColor: currentPage === totalPages ? 'transparent' : 'var(--card-bg)'
                          }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>}
    </div>
  );
};

export default AuditLogs;
